import { Component, Element, Event, EventEmitter, h, Host, Method, Prop } from "@stencil/core";

import { StzhClampClampEvent, StzhClampExpandEvent } from "../../index";

import { StzhClampLocalizedText } from "./stzh-clamp.localization";

/**
 * @slot - Slot for clamp text
 */
@Component({
  tag: "stzh-clamp",
  styleUrl: "stzh-clamp.scss",
})
export class StzhClamp {
  /** Translation strings */
  @Prop() localization: StzhClampLocalizedText;

  /** Line number */
  @Prop() lines: number = 1;
  /** Line number above small breakpoint */
  @Prop() linesSmall: number = 0;
  /** Line number above medium breakpoint */
  @Prop() linesMedium: number = 0;
  /** Line number above large breakpoint */
  @Prop() linesLarge: number = 0;
  /** Line number above ultra breakpoint */
  @Prop() linesUltra: number = 0;

  /** Whether expand link is shown */
  @Prop() showExpandLink: boolean = false;

  /** Whether clamping is expanded */
  @Prop({ mutable: true, reflect: true }) expanded: boolean = false;

  /** Whether the text is currently clamped (readonly) */
  @Prop({ mutable: true, reflect: true }) clamped: boolean = false;

  @Element() element: HTMLStzhClampElement;

  /** Event when clamping state changed */
  @Event() stzhClamp: EventEmitter<StzhClampClampEvent>;

  /** Event when expanding state changed */
  @Event() stzhExpand: EventEmitter<StzhClampExpandEvent>;

  @Method()
  async update() {
    this.textCloneElement.innerHTML = this.textElement.innerHTML;
    this.updateClamped();
  }

  private textElement: HTMLElement;
  private textCloneElement: HTMLElement;

  private debounceResize: number;
  private resizeObserver: ResizeObserver;
  private mutationObserver: MutationObserver;
  private renderPromiseResolve: (value?: unknown) => void;

  private updateClamped = () => {
    if (!this.textCloneElement) {
      return;
    }

    const beforeClamped = this.clamped;
    this.clamped = this.textCloneElement.scrollHeight > this.textCloneElement.clientHeight;

    if (beforeClamped !== this.clamped) {
      this.stzhClamp.emit({
        component: "stzh-clamp",
        clamped: this.clamped,
      });
    }
  };

  private handleResize = () => {
    if (this.debounceResize) {
      window.cancelAnimationFrame(this.debounceResize);
    }

    this.debounceResize = requestAnimationFrame(this.updateClamped);
  };

  private handleMutaton = () => {
    this.update();
  };

  private handleExpandClick = async () => {
    this.expanded = !this.expanded;
    await this.waitForNextRender();

    this.stzhExpand.emit({
      component: "stzh-clamp",
      expanded: this.expanded,
    });
  };

  private waitForNextRender() {
    return new Promise(resolve => (this.renderPromiseResolve = resolve));
  }

  async componentWillLoad() {
    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "clamp");
    }
  }

  componentDidLoad() {
    this.mutationObserver = new MutationObserver(this.handleMutaton);
    this.mutationObserver.observe(this.textElement, {
      childList: true,
      subtree: true,
    });
  }

  componentDidRender() {
    if (this.renderPromiseResolve) {
      this.renderPromiseResolve();
    }

    this.update();
  }

  connectedCallback() {
    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.element);
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  render() {
    const classes = {
      "stzh-clamp": true,
      "stzh-clamp--is-not-expanded": !this.expanded,
    };

    return (
      <Host
        style={{
          "--lines": this.lines ? this.lines.toString() : null,
          "--lines-small": this.linesSmall ? this.linesSmall.toString() : null,
          "--lines-medium": this.linesMedium ? this.linesMedium.toString() : null,
          "--lines-large": this.linesLarge ? this.linesLarge.toString() : null,
          "--lines-ultra": this.linesUltra ? this.linesUltra.toString() : null,
        }}
      >
        <div class={classes}>
          <div ref={el => (this.textElement = el as HTMLDivElement)} class="stzh-clamp__text">
            <slot></slot>
          </div>

          <div
            ref={el => (this.textCloneElement = el as HTMLDivElement)}
            class="stzh-clamp__text is-clone rs_skip"
            aria-hidden="true"
          ></div>

          {this.showExpandLink && this.clamped && (
            <stzh-link
              class="stzh-clamp__trigger"
              a11yTabindex="-1"
              aria-hidden="true"
              onClick={this.handleExpandClick}
            >
              {!this.expanded ? this.localization.showMore : this.localization.showLess}
            </stzh-link>
          )}
        </div>
      </Host>
    );
  }
}
